import * as XLSX from 'xlsx';

// 👉 Column ordering configuration
const COLUMN_ORDER_MAP: Record<string, string[]> = {
    "MFB_Player": ["ID", "PLAYER", "CLASS", "POS", "DATE", "TEAM", "OPP", "RES", "CONF", "PERIOD TYPE", "AVG YDS/COMP"],
    "MBB_Player": ["ID", "PLAYER", "POS", "DATE", "TEAM", "OPP", "MIN"],
    "WBB_Player": ["ID", "PLAYER", "POS", "DATE", "TEAM", "OPP", "TP"],
    // Add more mappings as needed
};

/**
 * Exports an array of objects to an XLSX file and triggers a download.
 * Each object in the array will be flattened, extracting all nested keys directly
 * without prepending parent object names (e.g., 'metadata_ID' becomes 'ID').
 *
 * This function also applies data transformations similar to the provided React Table component's
 * `getRowData` logic, including:
 * - 'RES' values (0 -> 'L', others -> 'W').
 * - Formatting numbers to two decimal places if they have more.
 * - Replacing null/undefined values with '-'.
 * - Excluding the 'PLAYER_ID' column from the export.
 * - Column ordering based on sport and entity type.
 *
 * Note: If there are duplicate keys across different nested objects (e.g., 'ID' in both 'metadata' and 'stats'),
 * the value from the last encountered key will overwrite previous ones.
 *
 * @param data - An array of objects to be exported.
 * @param fileName - The desired name for the downloaded XLSX file (e.g., 'my_data.xlsx').
 * @param sportCode - Sport code for column ordering (e.g., 'MFB', 'MBB', 'WBB').
 * @param entity - Entity type for column ordering (e.g., 'Player', 'Team').
 */
export function exportToXLSX(
    data: Record<string, any>[], 
    fileName: string = 'exported_data.xlsx',
    sportCode?: string,
    entity?: string
): void {
    if (!data || data.length === 0) {
        console.warn('No data provided for export.');
        return;
    }

    const EXPORT_SKIP_KEYS = new Set(['has_transfers', 'transfer_careers', 'transfer_totals', 'PLAYER_ID']);

    const flattenObject = (obj: Record<string, any>): Record<string, any> => {
        const flattened: Record<string, any> = {};
        for (const key in obj) {
            if (Object.prototype.hasOwnProperty.call(obj, key)) {
                if (EXPORT_SKIP_KEYS.has(key)) {
                    continue;
                }
                if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
                    Object.assign(flattened, flattenObject(obj[key]));
                } else {
                    flattened[key] = obj[key];
                }
            }
        }
        return flattened;
    };

    /**
     * Formats a cell value based on the header, mimicking the Table component's getRowData logic.
     * @param header - The column header.
     * @param value - The raw cell value.
     * @returns The formatted value for export.
     */
    const formatValueForExport = (header: string, value: any): string | number | null => {
        if (header === 'RES') {
            return value == 0 ? 'L' : 'W'; // Use '==' for loose comparison as in the original JS
        }

        if (header === 'PLAYER') {
            // For export, we just need the player name string, not a React Link component.
            return value || '-';
        }

        if (typeof value === 'number' && value.toString().includes(".")) {
            const decimalPart = value.toString().split(".")[1];
            if (decimalPart && decimalPart.length > 2) {
                return parseFloat(value.toFixed(2));
            }
        }

        if (value === null || value === undefined || value === '') {
            return "-";
        }

        return value;
    };

    /**
     * Orders columns based on sport code and entity type
     * @param allHeaders - Set of all available headers
     * @param sportCode - Sport code
     * @param entity - Entity type
     * @returns Ordered array of headers
     */
    const orderColumns = (allHeaders: Set<string>, sportCode?: string, entity?: string): string[] => {
        if (!sportCode || !entity) {
            return Array.from(allHeaders).sort();
        }

        const orderKey = `${sportCode}_${entity}`;
        const desiredOrder = COLUMN_ORDER_MAP[orderKey];
        
        if (!desiredOrder) {
            return Array.from(allHeaders).sort();
        }

        // Create case-insensitive mapping
        const headerMap: Record<string, string> = {};
        allHeaders.forEach(header => {
            headerMap[header.toUpperCase()] = header;
        });

        // Get ordered columns that exist in the data
        const orderedColumns: string[] = [];
        desiredOrder.forEach(desiredCol => {
            const upperDesired = desiredCol.toUpperCase();
            if (headerMap[upperDesired]) {
                orderedColumns.push(headerMap[upperDesired]);
            }
        });

        // Add remaining columns not in the desired order
        const remainingColumns = Array.from(allHeaders).filter(
            header => !orderedColumns.includes(header)
        );

        return [...orderedColumns, ...remainingColumns];
    };

    try {
        const flattenedData = data.map(item => flattenObject(item));

        let allHeaders: Set<string> = new Set();
        flattenedData.forEach(item => {
            Object.keys(item).forEach(key => {
                // Exclude 'PLAYER_ID' as per the Table component's rendering logic
                if (key !== "PLAYER_ID") {
                    allHeaders.add(key);
                }
            });
        });

        // 👉 Apply column ordering
        const orderedHeaders = orderColumns(allHeaders, sportCode, entity);

        // Prepare data for SheetJS, ensuring all rows have all columns and applying formatting
        const worksheetData: (string | number | boolean | null | undefined)[][] = [orderedHeaders];
        flattenedData.forEach(item => {
            const row: (string | number | boolean | null | undefined)[] = [];
            orderedHeaders.forEach(header => {
                const rawValue = item[header];
                const formattedValue = formatValueForExport(header, rawValue);
                row.push(formattedValue);
            });
            worksheetData.push(row);
        });

        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.aoa_to_sheet(worksheetData);

        XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');

        const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'binary' });

        const s2ab = (s: string): ArrayBuffer => {
            const buf = new ArrayBuffer(s.length);
            const view = new Uint8Array(buf);
            for (let i = 0; i < s.length; i++) view[i] = s.charCodeAt(i) & 0xFF;
            return buf;
        };
        const blob = new Blob([s2ab(wbout)], { type: 'application/octet-stream' });

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        console.log(`Data successfully exported to "${fileName}" with ${sportCode}_${entity} column ordering!`);

    } catch (error: any) {
        console.error("Error exporting to XLSX:", error);
    }
}